import React from 'react';
import { Box } from '@mui/material';
import { useFOVOverlay } from '../context/FOVOverlayContext';
import { useTranscodingProfiles } from '@/shared/context/TranscodingProfileContext';

/**
 * Width of a full-height 9:16 crop of the 16:9 player, as a percentage:
 * (9/16) / (16/9) = 31.64%. This is the export region at zoom 1 — i.e. when
 * the exporter frames a portrait clip exactly like a landscape one.
 */
const PORTRAIT_CROP_WIDTH_PCT = 31.64;

export const FOVOverlay: React.FC = () => {
  const { activePortraitGroupId, activeGroupIsFixedCamera } = useFOVOverlay();
  // 9:16 exports track tighter than 16:9 (C360-3094), so the exported region
  // is a smaller box than the full-height band — shrink both axes by the same
  // zoom the exporter applies, or the overlay promises framing nobody gets.
  // Fixed-camera groups are the exception: the exporter leaves their operator
  // framing alone, so their region really is the full-height band.
  const { portraitZoom } = useTranscodingProfiles();

  if (!activePortraitGroupId) {
    return null;
  }

  const zoom = activeGroupIsFixedCamera ? 1 : portraitZoom;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        // Contains the dimming shadow below to the player area.
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {/* Exported region, with everything outside it darkened */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${PORTRAIT_CROP_WIDTH_PCT / zoom}%`,
          height: `${100 / zoom}%`,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
        }}
      />
    </Box>
  );
};
